import { useMemo, useRef, useState } from 'react'
import { Bar, Doughnut } from 'react-chartjs-2'
import type { ChartData, ChartOptions, LegendItem, Chart } from 'chart.js'
import { ChartEvent } from 'chart.js/dist/core/core.plugins'

const BAR_CHART_BACKGROUND_COLOR = 'rgb(54, 162, 235)'
const NO_DATA_COLOR = 'rgb(152, 152, 152)'

const DONUT_CHART_COLORS = [
  'rgb(255, 99, 132)',
  'rgb(54, 162, 235)',
  'rgb(255, 205, 86)',
  'rgb(75, 192, 192)',
  'rgb(153, 102, 255)',
  'rgb(255, 159, 64)',
  'rgb(199, 199, 199)',
  'rgb(83, 102, 255)',
  'rgb(255, 99, 255)',
  'rgb(99, 255, 132)',
] as const

const CHART_STYLE = {
  maxWidth: 250,
  maxHeight: 250,
} as const

interface BarChartDataItem {
  month: string
  value: number
}

interface DonutChartDataItem {
  packageName: string
  value: string
}

interface BarChartProps {
  data: BarChartDataItem[]
  dataDonutChart?: DonutChartDataItem[]
}

const getDonutColor = (index: number): string => {
  return DONUT_CHART_COLORS[index % DONUT_CHART_COLORS.length]
}

export const BarChart = ({ data, dataDonutChart }: BarChartProps) => {
  const chartRef = useRef<Chart<'doughnut'>>(null)
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null)

  const barChartData = useMemo<ChartData<'bar'>>(() => {
    const labels = data?.map((item) => item.month) || []
    const values = data?.map((item) => item.value) || []

    return {
      labels,
      datasets: [
        {
          label: 'Value',
          data: values,
          borderWidth: 1,
          backgroundColor: BAR_CHART_BACKGROUND_COLOR,
        },
      ],
    }
  }, [data])

  const donutChartData = useMemo<ChartData<'doughnut'>>(() => {
    if (!dataDonutChart || dataDonutChart.length === 0) {
      return {
        labels: ['No data'],
        datasets: [
          {
            data: [100],
            borderWidth: 1,
            backgroundColor: [NO_DATA_COLOR],
            hoverOffset: 4
          },
        ],
      }
    }

    const labels = dataDonutChart.map((item) => item.packageName)
    const values = dataDonutChart.map((item) => Number(item.value))
    const colors = values.map((_, index) => getDonutColor(index))

    return {
      labels,
      datasets: [
        {
          label: 'Value',
          data: values,
          borderWidth: 1,
          backgroundColor: colors,
          hoverOffset: 4,
        },
      ],
    }
  }, [dataDonutChart, hoveredIndex])

  const donutChartOptions = useMemo<ChartOptions<'doughnut'>>(() => {
    const hasData = dataDonutChart && dataDonutChart.length > 0

    return {
      responsive: true,
      layout: {
        padding: 0,
      },
      plugins: {
        tooltip: {
          enabled: hasData,
        },
        legend: {
          position: 'bottom',
          labels: {
            boxWidth: 12,
            padding: 8,
          },
          onHover: (event: ChartEvent, legendItem: LegendItem) => {
            if (event.native?.target) {
              (event.native.target as HTMLElement).style.cursor = 'pointer'
            }
          },
          onLeave: (event: ChartEvent, legendItem: LegendItem, legend) => {
            if (event.native?.target) {
              (event.native.target as HTMLElement).style.cursor = 'default'
            }
          },
        },
      },
    }
  }, [dataDonutChart])

  return (
    <div className="flex items-center gap-x-10 flex-wrap gap-y-5">
      <div className="2xl:w-2/3 w-full">
        <Bar data={barChartData} />
      </div>

      <div className="min-w-[250px] flex-1 flex justify-center">
        <Doughnut
          ref={chartRef}
          data={donutChartData}
          options={donutChartOptions}
          style={CHART_STYLE}
          updateMode="resize"
        />
      </div>
    </div>
  )
}